import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type Announcements,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import RadioIcon from '@mui/icons-material/Radio';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../../api/client';
import { apiErrorText } from '../../api/errorText';
import type {
  LiveTvBouquet,
  LiveTvBouquetSummary,
  LiveTvChannel,
  LiveTvLineupChannel,
  LiveTvProviderSummary,
} from '../../api/types';
import { useApi } from '../../api/useApi';
import AsyncSection from '../AsyncSection';
import ChannelFilterBar from './ChannelFilterBar';
import type { ChannelFilter } from './lineup';
import { channelKey, compareChannels, emptyChannelFilter, matchesFilter, useProviderName } from './lineup';

/// Rows of available channels drawn before somebody asks for more.
const PAGE = 100;

/// One place in the bouquet being edited. The channel is null where its plugin no longer has it.
interface DraftEntry {
  key: string;
  providerId: string;
  channelId: string;
  channel: LiveTvChannel | null;
}

/// Changes that have not been saved yet, for one bouquet.
interface Draft {
  bouquetId: string;
  entries: DraftEntry[];
}

function fromServer(bouquet: LiveTvBouquet): DraftEntry[] {
  return bouquet.entries.map((entry) => ({
    key: channelKey(entry.providerId, entry.channelId),
    providerId: entry.providerId,
    channelId: entry.channelId,
    channel: entry.channel,
  }));
}

/// Bouquets the way a satellite receiver keeps them: named, ordered lists of channels from any
/// plugin. The list of bouquets on one side; for the chosen one, the channels there are and the
/// channels in it, side by side on a wide screen and one above the other on a phone.
///
/// Nothing is saved until somebody presses save. A bouquet is a list somebody arranges for a
/// while, and saving each move would leave half-arranged lists on every television in the house.
export default function BouquetEditor({ providers }: { providers: LiveTvProviderSummary[] | undefined }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const bouquets = useApi(['livetv', 'bouquets'], api.liveTvBouquets);
  const lineup = useApi(['livetv', 'lineup'], api.liveTvLineup);

  const [chosen, setChosen] = useState<string>();
  const selectedId =
    bouquets.data?.find((bouquet) => bouquet.id === chosen)?.id ?? bouquets.data?.[0]?.id;

  const bouquet = useApi(['livetv', 'bouquet', selectedId], () => api.liveTvBouquet(selectedId!), {
    enabled: selectedId !== undefined,
  });

  const [draft, setDraft] = useState<Draft>();
  const [pendingSwitch, setPendingSwitch] = useState<string>();
  const [naming, setNaming] = useState<{ bouquet?: LiveTvBouquetSummary; name: string }>();
  const [deleting, setDeleting] = useState<LiveTvBouquetSummary>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const saved = useMemo(() => (bouquet.data ? fromServer(bouquet.data) : []), [bouquet.data]);
  const entries = draft && draft.bouquetId === selectedId ? draft.entries : saved;
  const dirty =
    draft !== undefined
    && draft.bouquetId === selectedId
    && (draft.entries.length !== saved.length || draft.entries.some((entry, index) => entry.key !== saved[index].key));

  // Closing the tab or reloading with changes nobody saved asks first. Moving within the app is
  // covered by the unsaved-changes chip; the browser's own question is for leaving it.
  useEffect(() => {
    if (!dirty) {
      return undefined;
    }

    const warn = (event: BeforeUnloadEvent) => event.preventDefault();

    window.addEventListener('beforeunload', warn);

    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const lineupByKey = useMemo(
    () => new Map((lineup.data ?? []).map((entry) => [channelKey(entry.channel.providerId, entry.channel.id), entry])),
    [lineup.data],
  );

  const edit = (change: (entries: DraftEntry[]) => DraftEntry[]) => {
    if (selectedId) {
      setDraft({ bouquetId: selectedId, entries: change(entries) });
    }
  };

  const fail = (caught: unknown) => {
    const text = apiErrorText(caught, t);
    setError([text.message, text.remedy].filter(Boolean).join(' '));
  };

  const choose = (bouquetId: string) => {
    if (bouquetId === selectedId) {
      return;
    }

    if (dirty) {
      setPendingSwitch(bouquetId);
    } else {
      setDraft(undefined);
      setChosen(bouquetId);
    }
  };

  const save = async () => {
    if (!selectedId) {
      return;
    }

    setBusy(true);
    setError(undefined);

    try {
      const result = await api.setLiveTvBouquetChannels(
        selectedId,
        entries.map((entry) => ({ providerId: entry.providerId, channelId: entry.channelId })),
      );

      queryClient.setQueryData(['livetv', 'bouquet', selectedId], result);
      setDraft(undefined);
      void queryClient.invalidateQueries({ queryKey: ['livetv', 'bouquets'] });
      void queryClient.invalidateQueries({ queryKey: ['livetv', 'channels'] });
    } catch (caught) {
      fail(caught);
    } finally {
      setBusy(false);
    }
  };

  const submitName = async () => {
    if (!naming) {
      return;
    }

    setBusy(true);
    setError(undefined);

    try {
      if (naming.bouquet) {
        await api.renameLiveTvBouquet(naming.bouquet.id, naming.name);
      } else {
        const created = await api.createLiveTvBouquet(naming.name);

        if (!dirty) {
          setChosen(created.id);
        }
      }

      setNaming(undefined);
      bouquets.reload();
    } catch (caught) {
      fail(caught);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!deleting) {
      return;
    }

    setBusy(true);
    setError(undefined);

    try {
      await api.deleteLiveTvBouquet(deleting.id);

      if (deleting.id === selectedId) {
        setDraft(undefined);
        setChosen(undefined);
      }

      setDeleting(undefined);
      bouquets.reload();
    } catch (caught) {
      fail(caught);
    } finally {
      setBusy(false);
    }
  };

  // Which bouquet everybody who has not chosen one starts on. Pressing it on the bouquet that
  // already carries it hands it back, and then live television opens on every channel again.
  const makeDefault = async (bouquet: LiveTvBouquetSummary) => {
    setBusy(true);
    setError(undefined);

    try {
      if (bouquet.isDefault) {
        await api.clearDefaultLiveTvBouquet();
      } else {
        await api.setDefaultLiveTvBouquet(bouquet.id);
      }

      bouquets.reload();
    } catch (caught) {
      fail(caught);
    } finally {
      setBusy(false);
    }
  };

  const move = async (index: number, by: number) => {
    const list = bouquets.data ?? [];
    const target = index + by;

    if (target < 0 || target >= list.length) {
      return;
    }

    setError(undefined);

    try {
      const reordered = await api.reorderLiveTvBouquets(arrayMove(list, index, target).map((entry) => entry.id));
      queryClient.setQueryData(['livetv', 'bouquets'], reordered);
    } catch (caught) {
      fail(caught);
      bouquets.reload();
    }
  };

  const selected = bouquets.data?.find((entry) => entry.id === selectedId);

  return (
    <Stack spacing={2}>
      <Typography color="text.secondary" sx={{ maxWidth: '70ch' }}>
        {t('livetvChannels.bouquets.intro')}
      </Typography>

      <Typography color="text.secondary" sx={{ maxWidth: '70ch' }}>
        {t('livetvChannels.bouquets.defaultHint')}
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError(undefined)}>
          {error}
        </Alert>
      )}

      <AsyncSection loading={bouquets.loading} error={bouquets.error} onRetry={bouquets.reload}>
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: '260px minmax(0, 1fr)' },
            alignItems: 'start',
          }}
        >
          {/* The bouquets themselves: choose one, put them in order, make a new one. */}
          <Paper variant="outlined" sx={{ p: 1 }}>
            <Stack direction="row" sx={{ alignItems: 'center', px: 1, py: 0.5 }}>
              <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                {t('livetvChannels.bouquets.list')}
              </Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={() => setNaming({ name: '' })}>
                {t('livetvChannels.bouquets.create')}
              </Button>
            </Stack>

            {(bouquets.data ?? []).length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 2 }}>
                {t('livetvChannels.bouquets.none')}
              </Typography>
            ) : (
              <List dense disablePadding>
                {bouquets.data?.map((entry, index) => (
                  <Stack key={entry.id} direction="row" sx={{ alignItems: 'center' }}>
                    <ListItemButton
                      selected={entry.id === selectedId}
                      onClick={() => choose(entry.id)}
                      sx={{ borderRadius: 1, minHeight: 48 }}
                    >
                      <ListItemText
                        primary={entry.name}
                        secondary={entry.isDefault
                          ? t('livetvChannels.bouquets.defaultAndCount', { total: entry.channelCount })
                          : t('livetvChannels.bouquets.channelCount', { total: entry.channelCount })}
                        slotProps={{ primary: { noWrap: true } }}
                      />
                      {entry.isDefault && (
                        <StarIcon fontSize="small" color="primary" titleAccess={t('livetvChannels.bouquets.isDefault')} />
                      )}
                    </ListItemButton>
                    <IconButton
                      size="small"
                      disabled={index === 0}
                      aria-label={t('livetvChannels.bouquets.moveUp', { name: entry.name })}
                      onClick={() => void move(index, -1)}
                    >
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      disabled={index === (bouquets.data?.length ?? 0) - 1}
                      aria-label={t('livetvChannels.bouquets.moveDown', { name: entry.name })}
                      onClick={() => void move(index, 1)}
                    >
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
              </List>
            )}
          </Paper>

          {selected && (
            <Stack spacing={2} sx={{ minWidth: 0 }}>
              <Stack direction="row" useFlexGap spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography variant="h3" sx={{ fontSize: 20, flexGrow: 1, overflowWrap: 'anywhere' }}>
                  {selected.name}
                </Typography>
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => setNaming({ bouquet: selected, name: selected.name })}
                >
                  {t('livetvChannels.bouquets.rename')}
                </Button>
                <Button
                  size="small"
                  startIcon={selected.isDefault ? <StarIcon /> : <StarBorderIcon />}
                  disabled={busy}
                  onClick={() => void makeDefault(selected)}
                >
                  {selected.isDefault
                    ? t('livetvChannels.bouquets.unsetDefault')
                    : t('livetvChannels.bouquets.setDefault')}
                </Button>
                <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => setDeleting(selected)}>
                  {t('livetvChannels.bouquets.delete')}
                </Button>
              </Stack>

              <AsyncSection
                loading={bouquet.loading || lineup.loading}
                error={bouquet.error ?? lineup.error}
                onRetry={() => {
                  bouquet.reload();
                  lineup.reload();
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gap: 2,
                    gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) minmax(0, 1fr)' },
                    alignItems: 'start',
                  }}
                >
                  <AvailableChannels
                    lineup={lineup.data ?? []}
                    providers={providers}
                    inBouquet={new Set(entries.map((entry) => entry.key))}
                    onAdd={(added) =>
                      edit((current) => [
                        ...current,
                        ...added.map((channel) => ({
                          key: channelKey(channel.providerId, channel.id),
                          providerId: channel.providerId,
                          channelId: channel.id,
                          channel,
                        })),
                      ])
                    }
                  />

                  <BouquetChannels
                    entries={entries}
                    lineupByKey={lineupByKey}
                    providers={providers}
                    onChange={(next) => edit(() => next)}
                  />
                </Box>
              </AsyncSection>

              {/* Saving is deliberate, and what has not been saved yet is said plainly. It stays
                  in reach at the bottom of the screen however long the list is. */}
              <Paper
                variant="outlined"
                sx={{
                  position: 'sticky',
                  bottom: 8,
                  zIndex: 2,
                  p: 1.5,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  alignItems: 'center',
                }}
              >
                <Box sx={{ flexGrow: 1 }} aria-live="polite">
                  {dirty ? (
                    <Chip color="warning" label={t('livetvChannels.bouquets.unsaved')} />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {t('livetvChannels.bouquets.saved')}
                    </Typography>
                  )}
                </Box>
                <Button disabled={!dirty || busy} onClick={() => setDraft(undefined)}>
                  {t('livetvChannels.bouquets.discard')}
                </Button>
                <Button variant="contained" disabled={!dirty || busy} onClick={() => void save()}>
                  {t('common.save')}
                </Button>
              </Paper>
            </Stack>
          )}
        </Box>
      </AsyncSection>

      <Dialog open={naming !== undefined} onClose={() => setNaming(undefined)} fullWidth maxWidth="xs">
        <DialogTitle>
          {naming?.bouquet ? t('livetvChannels.bouquets.renameTitle') : t('livetvChannels.bouquets.createTitle')}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label={t('livetvChannels.bouquets.name')}
            value={naming?.name ?? ''}
            onChange={(event) => setNaming(naming && { ...naming, name: event.target.value })}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                void submitName();
              }
            }}
            slotProps={{ htmlInput: { maxLength: 100 } }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNaming(undefined)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            disabled={busy || !naming?.name.trim()}
            onClick={() => void submitName()}
          >
            {naming?.bouquet ? t('common.save') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleting !== undefined} onClose={() => setDeleting(undefined)} fullWidth maxWidth="xs">
        <DialogTitle>{t('livetvChannels.bouquets.deleteTitle', { name: deleting?.name ?? '' })}</DialogTitle>
        <DialogContent>
          <Typography>{t('livetvChannels.bouquets.deleteBody')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(undefined)}>{t('common.cancel')}</Button>
          <Button color="error" variant="contained" disabled={busy} onClick={() => void remove()}>
            {t('livetvChannels.bouquets.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={pendingSwitch !== undefined} onClose={() => setPendingSwitch(undefined)} fullWidth maxWidth="xs">
        <DialogTitle>{t('livetvChannels.bouquets.discardTitle')}</DialogTitle>
        <DialogContent>
          <Typography>{t('livetvChannels.bouquets.discardBody')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingSwitch(undefined)}>{t('livetvChannels.bouquets.keepEditing')}</Button>
          <Button
            color="warning"
            variant="contained"
            onClick={() => {
              setDraft(undefined);
              setChosen(pendingSwitch);
              setPendingSwitch(undefined);
            }}
          >
            {t('livetvChannels.bouquets.discard')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

/// Every channel there is, to pick from: one at a time, or several ticked and added together.
function AvailableChannels({
  lineup,
  providers,
  inBouquet,
  onAdd,
}: {
  lineup: LiveTvLineupChannel[];
  providers: LiveTvProviderSummary[] | undefined;
  inBouquet: Set<string>;
  onAdd: (channels: LiveTvChannel[]) => void;
}) {
  const { t } = useTranslation();
  const providerName = useProviderName(providers);

  const [filter, setFilter] = useState<ChannelFilter>(emptyChannelFilter);
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [shown, setShown] = useState(PAGE);

  const filtered = useMemo(
    () =>
      lineup
        .filter((entry) => matchesFilter(entry.channel, filter, [entry.original.name]))
        .map((entry) => entry.channel)
        .sort(compareChannels),
    [lineup, filter],
  );

  const addable = filtered.filter((channel) => !inBouquet.has(channelKey(channel.providerId, channel.id)));
  const chosen = addable.filter((channel) => ticked.has(channelKey(channel.providerId, channel.id)));

  const add = (channels: LiveTvChannel[]) => {
    onAdd(channels);
    setTicked(new Set());
  };

  return (
    <Paper variant="outlined" sx={{ p: 1.5, minWidth: 0 }}>
      <Stack spacing={1.5}>
        <Typography variant="subtitle2">{t('livetvChannels.bouquets.available')}</Typography>

        <ChannelFilterBar
          filter={filter}
          onChange={(next) => {
            setFilter(next);
            setShown(PAGE);
          }}
          providers={providers}
        />

        <Stack direction="row" useFlexGap spacing={1} sx={{ flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant="contained"
            startIcon={<PlaylistAddIcon />}
            disabled={chosen.length === 0}
            onClick={() => add(chosen)}
          >
            {t('livetvChannels.bouquets.addSelected', { total: chosen.length })}
          </Button>
          <Button size="small" disabled={addable.length === 0} onClick={() => add(addable)}>
            {t('livetvChannels.bouquets.addAllShown', { total: addable.length })}
          </Button>
        </Stack>

        <Box sx={{ maxHeight: { md: '60vh' }, overflowY: { md: 'auto' } }}>
          <List dense disablePadding>
            {filtered.slice(0, shown).map((channel) => {
              const key = channelKey(channel.providerId, channel.id);
              const already = inBouquet.has(key);

              return (
                <Stack key={key} direction="row" sx={{ alignItems: 'center', minHeight: 48 }}>
                  <Checkbox
                    checked={already || ticked.has(key)}
                    disabled={already}
                    onChange={(event) => {
                      const next = new Set(ticked);

                      if (event.target.checked) {
                        next.add(key);
                      } else {
                        next.delete(key);
                      }

                      setTicked(next);
                    }}
                    slotProps={{ input: { 'aria-label': t('livetvChannels.bouquets.select', { name: channel.name }) } }}
                  />
                  <ChannelLabel channel={channel} provider={providerName(channel.providerId)} />
                  <IconButton
                    disabled={already}
                    aria-label={t('livetvChannels.bouquets.addOne', { name: channel.name })}
                    onClick={() => add([channel])}
                  >
                    <AddIcon />
                  </IconButton>
                </Stack>
              );
            })}
          </List>

          {filtered.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              {t('livetvChannels.bouquets.noMatch')}
            </Typography>
          )}

          {filtered.length > shown && (
            <Button onClick={() => setShown(shown + PAGE)}>
              {t('livetvChannels.channels.more', { total: filtered.length - shown })}
            </Button>
          )}
        </Box>
      </Stack>
    </Paper>
  );
}

/// The bouquet as it stands: in order, rearranged by dragging the handle - with a mouse, a finger
/// or the keyboard - or with the buttons beside each channel, which are what a remote or a screen
/// reader reaches most easily.
function BouquetChannels({
  entries,
  lineupByKey,
  providers,
  onChange,
}: {
  entries: DraftEntry[];
  lineupByKey: Map<string, LiveTvLineupChannel>;
  providers: LiveTvProviderSummary[] | undefined;
  onChange: (entries: DraftEntry[]) => void;
}) {
  const { t } = useTranslation();
  const providerName = useProviderName(providers);

  const sensors = useSensors(
    // A few pixels of movement before a drag begins, so a tap on the handle stays a tap.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const nameOf = (key: string | number) => {
    const entry = entries.find((candidate) => candidate.key === key);

    return entry?.channel?.name ?? entry?.channelId ?? String(key);
  };

  const positionOf = (key: string | number | undefined) => entries.findIndex((entry) => entry.key === key) + 1;

  // What a screen reader says while somebody drags, in their language rather than the library's.
  const announcements: Announcements = {
    onDragStart: ({ active }) =>
      t('livetvChannels.drag.start', { name: nameOf(active.id), position: positionOf(active.id) }),
    onDragOver: ({ active, over }) =>
      over ? t('livetvChannels.drag.over', { name: nameOf(active.id), position: positionOf(over.id) }) : undefined,
    onDragEnd: ({ active, over }) =>
      over
        ? t('livetvChannels.drag.end', { name: nameOf(active.id), position: positionOf(over.id) })
        : t('livetvChannels.drag.cancel', { name: nameOf(active.id) }),
    onDragCancel: ({ active }) => t('livetvChannels.drag.cancel', { name: nameOf(active.id) }),
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) {
      return;
    }

    const from = entries.findIndex((entry) => entry.key === active.id);
    const to = entries.findIndex((entry) => entry.key === over.id);

    if (from >= 0 && to >= 0) {
      onChange(arrayMove(entries, from, to));
    }
  };

  const missing = entries.filter((entry) => entry.channel === null).length;

  return (
    <Paper variant="outlined" sx={{ p: 1.5, minWidth: 0 }}>
      <Stack spacing={1.5}>
        <Stack direction="row" useFlexGap spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
            {t('livetvChannels.bouquets.inBouquet', { total: entries.length })}
          </Typography>
          {missing > 0 && (
            <Button
              size="small"
              color="warning"
              onClick={() => onChange(entries.filter((entry) => entry.channel !== null))}
            >
              {t('livetvChannels.bouquets.removeMissing', { total: missing })}
            </Button>
          )}
        </Stack>

        <Typography variant="caption" color="text.secondary">
          {t('livetvChannels.bouquets.numbering')}
        </Typography>

        {entries.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            {t('livetvChannels.bouquets.empty')}
          </Typography>
        ) : (
          <Box sx={{ maxHeight: { md: '60vh' }, overflowY: { md: 'auto' } }}>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={onDragEnd}
              accessibility={{
                announcements,
                screenReaderInstructions: { draggable: t('livetvChannels.drag.instructions') },
              }}
            >
              <SortableContext items={entries.map((entry) => entry.key)} strategy={verticalListSortingStrategy}>
                <List dense disablePadding>
                  {entries.map((entry, index) => (
                    <SortableRow
                      key={entry.key}
                      entry={entry}
                      index={index}
                      count={entries.length}
                      number={lineupByKey.get(entry.key)?.override?.number ?? String(index + 1)}
                      provider={providerName(entry.providerId)}
                      onMove={(by) => onChange(arrayMove(entries, index, index + by))}
                      onRemove={() => onChange(entries.filter((candidate) => candidate.key !== entry.key))}
                    />
                  ))}
                </List>
              </SortableContext>
            </DndContext>
          </Box>
        )}
      </Stack>
    </Paper>
  );
}

function SortableRow({
  entry,
  index,
  count,
  number,
  provider,
  onMove,
  onRemove,
}: {
  entry: DraftEntry;
  index: number;
  count: number;
  number: string;
  provider: string;
  onMove: (by: number) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: entry.key,
  });

  const name = entry.channel?.name ?? entry.channelId;

  return (
    <Stack
      ref={setNodeRef}
      direction="row"
      sx={{
        alignItems: 'center',
        minHeight: 48,
        borderBlockEnd: '1px solid',
        borderColor: 'divider',
        backgroundColor: isDragging ? 'action.selected' : 'background.paper',
        position: 'relative',
        zIndex: isDragging ? 1 : 0,
      }}
      // Only ever up and down: a sideways offset means nothing in a list, and would point the
      // wrong way in a language written right to left.
      style={{ transform: CSS.Translate.toString(transform && { ...transform, x: 0 }), transition }}
    >
      <IconButton
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        aria-label={t('livetvChannels.bouquets.drag', { name })}
        sx={{ cursor: 'grab', touchAction: 'none' }}
      >
        <DragIndicatorIcon />
      </IconButton>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ width: 40, flexShrink: 0, textAlign: 'end', paddingInlineEnd: 1, fontVariantNumeric: 'tabular-nums' }}
      >
        {number}
      </Typography>

      {entry.channel ? (
        <ChannelLabel channel={entry.channel} provider={provider} />
      ) : (
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
            <WarningAmberIcon fontSize="small" color="warning" />
            <Typography variant="body2" color="warning.main" noWrap>
              {t('livetvChannels.bouquets.missing')}
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
            {provider} · {entry.channelId}
          </Typography>
        </Box>
      )}

      <IconButton
        size="small"
        disabled={index === 0}
        aria-label={t('livetvChannels.bouquets.moveUp', { name })}
        onClick={() => onMove(-1)}
      >
        <ArrowUpwardIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        disabled={index === count - 1}
        aria-label={t('livetvChannels.bouquets.moveDown', { name })}
        onClick={() => onMove(1)}
      >
        <ArrowDownwardIcon fontSize="small" />
      </IconButton>
      <IconButton size="small" aria-label={t('livetvChannels.bouquets.removeOne', { name })} onClick={onRemove}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </Stack>
  );
}

/// Name, provider, and the two things worth knowing at a glance: radio, and scrambled.
function ChannelLabel({ channel, provider }: { channel: LiveTvChannel; provider: string }) {
  const { t } = useTranslation();

  return (
    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', minWidth: 0 }}>
        <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
          {channel.name}
        </Typography>
        {channel.kind === 'Radio' && (
          <RadioIcon sx={{ fontSize: 16 }} color="action" aria-label={t('livetvChannels.kind.Radio')} />
        )}
        {channel.isEncrypted && (
          <LockIcon sx={{ fontSize: 14 }} color="action" aria-label={t('livetvChannels.channels.encrypted')} />
        )}
      </Stack>
      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
        {provider}
      </Typography>
    </Box>
  );
}
